import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { useTranslation } from '../../hooks/useTranslation';

interface FeedbackButtonProps {
  className?: string;
  'data-testid'?: string;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  className = '',
  'data-testid': testId = 'feedback-button'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation();

  // Close form when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus message input when form opens
  useEffect(() => {
    if (isOpen && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen && !showSuccess) {
      setMessage('');
      setEmail('');
    }
  }, [isOpen, showSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) return;
    
    setIsSending(true);

    try {
      const { error } = await supabase
        .from('feedback')
        .insert([{ message: message.trim(), email: email.trim() || null }]);

      if (error) throw error;

      setShowSuccess(true);
      setMessage('');
      setEmail('');
      
      // Close form after showing success message
      setTimeout(() => {
        setIsOpen(false);
        setShowSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      {/* Floating feedback button */}
      <button
        onClick={handleToggle}
        className={`
          fixed bottom-6 right-6 z-40
          flex items-center gap-2 px-4 py-3
          bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90
          text-white font-medium rounded-full shadow-lg
          transition-all duration-200 hover:scale-105
          ${isOpen ? 'scale-95 opacity-75' : ''}
        `}
        aria-label={t('feedback.button') || 'Feedback?'}
      >
        <MessageSquare className="w-5 h-5" />
        <span className="hidden sm:inline">
          {t('feedback.button') || 'Feedback?'}
        </span>
      </button>

      {/* Feedback form */}
      {isOpen && (
        <div 
          ref={formRef}
          className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-lg shadow-2xl border border-border-primary p-6"
          onKeyDown={handleKeyDown}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              {t('feedback.title') || 'Help us improve!'}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 text-text-muted hover:text-text-secondary transition-colors"
              aria-label="Close feedback form"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Success state */}
          {showSuccess ? (
            <div className="text-center py-4">
              <div className="text-green-600 mb-2">
                <MessageSquare className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-[var(--text-primary)] font-medium">
                {t('feedback.success') || 'Thank you for your feedback! 🎉'}
              </p>
            </div>
          ) : (
            /* Feedback form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <textarea
                  ref={messageInputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('feedback.placeholder.message') || 'What could be improved?'}
                  className="w-full px-3 py-2 border border-border-primary rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-opacity-50"
                  rows={4}
                  required
                  disabled={isSending}
                />
              </div>

              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('feedback.placeholder.email') || 'Your email (optional)'}
                  className="w-full px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-opacity-50"
                  disabled={isSending}
                />
              </div>

              <button
                type="submit"
                disabled={!message.trim() || isSending}
                className={`
                  w-full flex items-center justify-center gap-2 px-4 py-2
                  bg-[var(--brand-primary)] text-white font-medium rounded-lg
                  transition-colors duration-200
                  ${(!message.trim() || isSending)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[var(--brand-primary)]/90'
                  }
                `}
              >
                {isSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('feedback.sending') || 'Sending...'}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{t('feedback.submit') || 'Send Feedback'}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default FeedbackButton;